import { CheckCircle2, Circle, CircleDashed, Loader2, RefreshCw, XCircle } from "lucide-react";

import type { OnboardingStepDetail, OnboardingStepStatus } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STEP_LABELS: Record<string, string> = {
	fly_provision: "Fly app + Postgres",
	dns_record: "DNS + TLS cert",
	base_secrets: "Base secrets",
	deploy: "Deploy portal",
	bootstrap_admin: "Bootstrap admin",
	oauth_secrets: "OAuth secrets",
	register: "Register + health check",
};

function StatusBadge({ status }: { status: OnboardingStepStatus }) {
	switch (status) {
		case "succeeded":
			return (
				<Badge variant="success">
					<CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
					Succeeded
				</Badge>
			);
		case "running":
			return (
				<Badge variant="outline">
					<Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
					Running
				</Badge>
			);
		case "failed":
			return (
				<Badge variant="destructive">
					<XCircle className="mr-1 h-3 w-3" aria-hidden />
					Failed
				</Badge>
			);
		case "skipped":
			return (
				<Badge variant="outline" className="text-muted-foreground">
					<CircleDashed className="mr-1 h-3 w-3" aria-hidden />
					Skipped
				</Badge>
			);
		default:
			return (
				<Badge variant="outline" className="text-muted-foreground">
					<Circle className="mr-1 h-3 w-3" aria-hidden />
					Pending
				</Badge>
			);
	}
}

interface Props {
	steps: OnboardingStepDetail[];
	liveStatuses: Record<number, OnboardingStepStatus>;
	liveErrors: Record<number, string | null>;
	expandedOrdinal: number | null;
	onToggleExpand: (ordinal: number) => void;
	onRetry: (ordinal: number) => void;
	retryPending: boolean;
}

export function StepTracker({
	steps,
	liveStatuses,
	liveErrors,
	expandedOrdinal,
	onToggleExpand,
	onRetry,
	retryPending,
}: Props) {
	return (
		<ul className="space-y-2">
			{steps.map((step) => {
				const status = liveStatuses[step.ordinal] ?? step.status;
				const errorMsg = liveErrors[step.ordinal] ?? step.error_message;
				const isExpanded = expandedOrdinal === step.ordinal;
				return (
					<li key={step.ordinal} className="rounded-md border bg-card">
						<button
							type="button"
							onClick={() => onToggleExpand(step.ordinal)}
							className="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-accent/30"
						>
							<div className="flex items-center gap-3">
								<span className="font-mono text-xs text-muted-foreground">{step.ordinal}.</span>
								<div>
									<div className="font-medium text-sm">{STEP_LABELS[step.kind] ?? step.kind}</div>
									{errorMsg ? (
										<p className="mt-1 max-w-xl truncate text-xs text-destructive">{errorMsg}</p>
									) : null}
								</div>
							</div>
							<div className="flex items-center gap-2">
								{step.attempt_count > 1 ? (
									<span
										className="text-xs text-muted-foreground"
										title={`${step.attempt_count} attempts`}
									>
										#{step.attempt_count}
									</span>
								) : null}
								<StatusBadge status={status} />
							</div>
						</button>
						{status === "failed" ? (
							<div className="flex justify-end gap-2 border-t p-2">
								<Button
									size="sm"
									variant="outline"
									onClick={() => onRetry(step.ordinal)}
									disabled={retryPending}
								>
									<RefreshCw className="mr-1 h-3 w-3" aria-hidden />
									{retryPending ? "Retrying…" : "Retry step"}
								</Button>
							</div>
						) : null}
						{isExpanded ? (
							<div className="border-t p-3 text-xs">
								<StepMetaGrid step={step} />
							</div>
						) : null}
					</li>
				);
			})}
		</ul>
	);
}

function StepMetaGrid({ step }: { step: OnboardingStepDetail }) {
	return (
		<dl className="grid grid-cols-2 gap-3">
			<div>
				<dt className="uppercase tracking-wide text-muted-foreground">Kind</dt>
				<dd className="font-mono">{step.kind}</dd>
			</div>
			<div>
				<dt className="uppercase tracking-wide text-muted-foreground">Attempts</dt>
				<dd>{step.attempt_count}</dd>
			</div>
			<div>
				<dt className="uppercase tracking-wide text-muted-foreground">Started</dt>
				<dd>{step.started_at ? new Date(step.started_at).toLocaleString() : "—"}</dd>
			</div>
			<div>
				<dt className="uppercase tracking-wide text-muted-foreground">Finished</dt>
				<dd>{step.finished_at ? new Date(step.finished_at).toLocaleString() : "—"}</dd>
			</div>
			{step.result_payload ? (
				<div className="col-span-2">
					<dt className="uppercase tracking-wide text-muted-foreground">Payload</dt>
					<dd>
						<pre className="overflow-x-auto rounded bg-muted p-2 text-[11px]">
							{JSON.stringify(step.result_payload, null, 2)}
						</pre>
					</dd>
				</div>
			) : null}
		</dl>
	);
}
